import type { TFunction } from 'i18next';
import { formatExactDollars, toSupportedCurrency } from '@/lib/currency';

/**
 * Текст цены ручного запуска — один на оба экрана, что её называют:
 * подтверждение накопленного (BacklogCard) и запуск выбранного или одной
 * записи (StartQuoteConfirmDialog). Две копии одной формулировки расходились
 * бы на первой же правке — а это слова, на которые клиент соглашается
 * платить (#1389).
 *
 * Цена приходит с сервера и здесь не пересчитывается.
 */
export interface QuoteForMessage {
  records: number;
  minutes: number;
  chats?: number | null;
  unknownDurationCalls?: number | null;
  priceUsd: number;
  currency: string;
  /** Из `records` уже оплачено раньше — звонок при расшифровке, чат при
   * анализе (только у `/start-quote`). */
  alreadyCharged?: number;
  /**
   * Постоплата (договор) платит полную ставку мимо минут тарифа: сумма
   * называется «по договору», а не «сверх тарифа», и ноль — «ничего не
   * спишется», а не «покрывается минутами тарифа» (code review r2 на #1454).
   * Нет поля — прежние слова: ответ сервера до него.
   */
  billingMode?: 'prepaid' | 'postpaid' | null;
  /**
   * Выбранные записи источника с остановленной обработкой (#2709) — в
   * `records` они не входят и не запустятся. Нет поля — их нет.
   */
  processingPaused?: number | null;
}

export interface QuoteMessageParts {
  base: string;
  /** Часть набора уже оплачена — второго списания за неё не будет. */
  alreadyChargedNote: string | null;
  /** Откуда у переписки минуты. */
  chatNote: string | null;
  /** Почему сумма — нижняя граница. */
  unpricedNote: string | null;
  /** Сколько записей набора не запустится: обработка их источника остановлена. */
  processingPausedNote: string | null;
}

export function quoteMessageParts(
  t: TFunction,
  prefix: 'records.start' | 'records.backlog',
  quote: QuoteForMessage
): QuoteMessageParts {
  const unpricedCalls = quote.unknownDurationCalls ?? 0;
  const alreadyCharged = quote.alreadyCharged ?? 0;
  const pausedCount = quote.processingPaused ?? 0;
  const params = { count: quote.records, records: quote.records, minutes: quote.minutes };
  // «Бесплатно» не говорится, если в наборе есть звонок без известной
  // длительности: ноль в сумме тогда — отсутствие данных, а не цены.
  const isFree = quote.priceUsd <= 0 && unpricedCalls === 0;
  const amount = formatExactDollars(quote.priceUsd, toSupportedCurrency(quote.currency));

  // Нулевая цена называет настоящую причину. Если весь набор уже оплачен
  // раньше (звонок — при расшифровке, чат — при анализе, а потом запись
  // остановил ноль баланса), «покрыто минутами тарифа» было бы неправдой — у
  // организации на паузе их как раз нет. Текст не называет шаг оплаты: он
  // разный у звонка и у чата.
  const allAlreadyPaid = isFree && alreadyCharged > 0 && alreadyCharged >= quote.records;
  // Смешанный бесплатный набор: часть оплачена раньше, остаток в тарифе.
  // Одна строка с обеими причинами, а не «покрыто минутами тарифа» и
  // уточнение следом — главная строка не должна называть одну причину из двух
  // (#1415). Минуты в ней — минуты остатка: оплаченное в них не входит.
  //
  // У постоплаты минут тарифа нет, и «остаток покрыт минутами тарифа» был бы
  // неправдой: её смешанный бесплатный набор называется «ничего не спишется»,
  // а оплаченная часть — отдельной строкой.
  const postpaid = quote.billingMode === 'postpaid';
  const partlyPaid = isFree && alreadyCharged > 0 && !allAlreadyPaid && !postpaid;
  const suffix = postpaid ? 'Postpaid' : '';
  const base = allAlreadyPaid
    ? t(`${prefix}.confirmMessageAlreadyPaid`, params)
    : partlyPaid
      ? t(`${prefix}.confirmMessageFreePartlyPaid`, { ...params, paid: alreadyCharged })
      : isFree
        ? t(`${prefix}.confirmMessageFree${suffix}`, params)
        : unpricedCalls > 0
          ? t(`${prefix}.confirmMessageFrom${suffix}`, { ...params, amount })
          : t(`${prefix}.confirmMessage${suffix}`, { ...params, amount });

  const chats = quote.chats ?? 0;
  return {
    base,
    alreadyChargedNote:
      alreadyCharged > 0 && !allAlreadyPaid && !partlyPaid
        ? t(`${prefix}.confirmAlreadyCharged`, { count: alreadyCharged })
        : null,
    chatNote:
      chats > 0 ? t('records.backlog.confirmChats', { chats, count: chats }) : null,
    unpricedNote:
      unpricedCalls > 0
        ? t('records.backlog.confirmUnknownDuration', {
            calls: unpricedCalls,
            count: unpricedCalls,
          })
        : null,
    processingPausedNote:
      pausedCount > 0
        ? t('records.start.resultProcessingPausedNote', { count: pausedCount })
        : null,
  };
}

export function formatQuoteMessage(
  t: TFunction,
  prefix: 'records.start' | 'records.backlog',
  quote: QuoteForMessage
): string {
  const { base, alreadyChargedNote, chatNote, unpricedNote, processingPausedNote } =
    quoteMessageParts(t, prefix, quote);
  return [base, alreadyChargedNote, chatNote, unpricedNote, processingPausedNote]
    .filter(Boolean)
    .join(' ');
}
